import React, { useEffect, useState, useRef } from "react";
import { useSelector } from 'react-redux';
import { Chart } from "react-google-charts";
import TapSelect from "../../../components/ui/TapSelect";
import HttpAPICall from '../../../services/HttpAPICall';
import tapIcon from "../../../services/TapIcon";
import moment from "moment";
import UpdateMyDashboardForm from "../../settings/configurationGeneral/myDashboard/UpdateMyDashboardForm";
import Loader from "../../../components/ui/Loader/Loader";
import { APP_BASE_URL_2 } from '../../../services/TapApiUrls';
import DateService from "../../../services/DateService";

const LogisticWidget = (props) => {
    
    const widgetUrl             =  APP_BASE_URL_2 + "/logistic/logistic_status_widget"
    const updateMyDashboardRef  =   useRef();
    const access_token          =   useSelector(state => state.auth.access_token);
    const periods               =   useSelector(state => state.app && state.app.all_periods ?
        [...state.app.all_periods.map((s) => ({ value: s.key, label: `${s.display}` }))] : []);
    const widget_keys           =   useSelector(state => state.app.user_info?.widget_keys || []);

    const [widgetData, setWidgetData]   =   useState(null) 
    const [formData, setFormData]       =   useState({
        all_periods  :   [],
        userFilters    : [
            { value: 'tagged_me', label: 'My Tagged Logistic' },
            { value: 'created_by_me', label: 'Logistic Created By Me' },
        ],
        filterData  :   {
            period: 'current_quarter',
            tagged_user_filter  :   ""
        }
    })

    const [logisticData, setLogisticData] = useState({
        loading         :   true,
        data            :   [],
        remark_time     :   '',
    });
    const [reload, setReload] = useState(false);
    const hasInitialized = useRef(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        initializeFormFilter();
        
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            loadDataOnInit();
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (periods.length > 0 && formData.all_periods.length === 0) {
            setFormData(pre => ({
                ...pre,
                all_periods: periods
            }));
        }
    }, [periods]);

    useEffect(() => {
        if (widget_keys.length > 0 && !widgetData) {
            setWidgetData(widget_keys.find(st => st.key == "logistic_status_wise_count") || null);
        }
    }, [widget_keys]);

    useEffect(() => {
        if (reload) {
            getLogisticData(true);
            setReload(false);
        }
    }, [reload]);

    const loadDataOnInit = () => {
        // Check localStorage first
        if (localStorage.getItem('logisticWidget')) {
            let logisticDataCache = JSON.parse(localStorage.getItem('logisticWidget'));
            if (logisticDataCache) {
                let differenceInSeconds = DateService.differenceInSeconds(moment(logisticDataCache.time, 'DD-MM-YYYY HH:mm A').format());
                if (differenceInSeconds <= 86400) {
                    // Use cached data immediately
                    setLogisticData({
                        data: logisticDataCache.data,
                        loading: false,
                        remark_time: logisticDataCache.time,
                    });
                    return;
                } else {
                    localStorage.removeItem('logisticWidget');
                }
            }
        }
        
        // If no valid cache, fetch new data after timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        getLogisticData(false);
        // timeoutRef.current = setTimeout(() => {
        //     getLogisticData(false);
        // }, 1000 * (props.indexProps || 12));
    };

    const initializeFormFilter = () => {
        if (periods.length > 0) {
            setFormData(pre => ({
                ...pre,
                all_periods: periods
            }));
        }
        if (widget_keys.length > 0) {
            setWidgetData(widget_keys.find(st => st.key == "logistic_status_wise_count") || null);
        }
    };

    const getLogisticData = (isReload = false) => {
        setLogisticData(pre => ({...pre, loading: true}));
        
        let params = {
            period: formData.filterData.period,
            tagged_user_filter: formData.filterData.tagged_user_filter,
            reload: isReload ? 'reload' : false
        };
        
        HttpAPICall.withAthorization('GET', widgetUrl, access_token, params, {}, (response) => {
            const res = response.data;
            setLogisticData({
                data: res.data,
                remark_time: res.time,
                loading: false
            });
            
            // Cache only when no filters are applied
            if ( !formData.filterData.tagged_user_filter) {
                localStorage.setItem('logisticWidget', JSON.stringify(res));
            }
            if (isReload) {
                localStorage.setItem('logisticWidget', JSON.stringify(res));
            }
        }).catch((error) => {
            console.error("Error fetching logistic data:", error);
            setLogisticData(pre => ({...pre, loading: false}));
        });
    };

    const handleFilterChange = (filterType, value) => {
        setFormData(pre => ({
            ...pre,
            filterData: {
                ...pre.filterData,
                [filterType]: value
            }
        }));
        // Call API immediately on filter change
        setTimeout(() => {
            getLogisticData(false);
        }, 100);
    };

    const pinMyDashboardHandler = (widgetData = null) => {
        updateMyDashboardRef.current.updateMyDashboardModalInit(widgetData, { key: "logistic_status_wise_count", name: "Logistic " });
    };
    
    const handleCountClick = (data) => {
        const statusId = data.id;
        const taggedUserFilter = formData.filterData.tagged_user_filter;
        const dateRange = formData.filterData.period;

        const params = new URLSearchParams();
        if (statusId) {
            params.append('status_id', statusId);
        }
        if (taggedUserFilter) {
            params.append('tagged_user_filter', taggedUserFilter);
        }
        if (dateRange) {
            params.append('date_range', dateRange);
        }

        const url = `/logistic_list?${params.toString()}`;
        window.open(url, '_blank');
    };
    
    const totalValue = logisticData.data.reduce((acc, d) => acc + (parseInt(d.count) > 0 ? parseInt(d.count) : 0), 0);
    
    return (
        <div className="card mt-3">
            <div className="card-body">
                <div className="row">
                    <div className="col-5">
                        <h6 className="fw-bold primary_color">Logistic Widget</h6>
                    </div> 
                    <div className="col-3">
                        <TapSelect
                            options={formData.userFilters}
                            changeEvent={(selectedOption) => {
                                handleFilterChange('tagged_user_filter', selectedOption ? selectedOption.value : "");
                            }}
                            value={formData.userFilters.find(u => u.value == formData.filterData.tagged_user_filter)}
                            isSearchable={true}
                            isClearable={true}
                            containerHeight="33px"
                            fontSize="93%"
                            width="99%"
                            placeholder="All Logistic"
                        />
                    </div>
                    <div className="col-3 p0">
                        <TapSelect
                            options={formData.all_periods}
                            changeEvent={(selectedOption) => {
                                handleFilterChange('period', selectedOption ? selectedOption.value : "");
                            }}
                            value={formData.all_periods.find(u => u.value == formData.filterData.period)}
                            isSearchable={true}
                            isClearable={true}
                            containerHeight="33px"
                            fontSize="93%"
                            width="99%"
                            placeholder="All Periods"
                        />
                    </div>
                    <div className="col-1 px-0 mx-0 text-center">
                        <tapIcon.imageIcon icon={tapIcon.ListActionIcon} alt="action-icon" className="img-fluid px-2 btn btn-light btn-block" data-bs-toggle="dropdown" href="#" role="button" aria-expanded="false" />
                        <ul className="dropdown-menu dropdown-menu-end">
                            <li className="dropdown-item" style={{ cursor: "pointer" }} onClick={() => setReload(true)}>Refresh</li>
                            <li className="dropdown-item" style={{ cursor: "pointer" }} onClick={() => pinMyDashboardHandler(widgetData)}>{!widgetData ? "Pinned to My Dashboard" : "Unpinned from My Dashboard"}</li>
                        </ul>
                    </div>
                </div>
                <div className="row mt-2">
                    <div className="col-6">
                        <div style={{ overflowY: "auto", maxHeight: "300px" }}>
                            <table className="table table-hover table-bordered table-borderless bg-white">
                                <thead className="table-secondary">
                                    <tr className="text-center">
                                        <th style={{ width: "50%" }} className="text-center">Status</th>
                                        <th className="text-center" style={{ width: "50%" }}>Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logisticData.loading ? (
                                        <tr><td colSpan="6"><Loader /></td></tr>
                                    ) : (logisticData.data && logisticData.data.length > 0 ? (
                                        logisticData.data.map((w, index) => (
                                            <tr key={index} className="text-center">
                                                <td>{w.name}</td>
                                                <td>
                                                    {w.count ? <>
                                                        <span className="text-primary" style={{ cursor: 'pointer' }} onClick={() => handleCountClick(w)}>
                                                            {w.count}
                                                        </span>
                                                    </> : "-"}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="6" className="text-center">No Records</td></tr>
                                    ))}
                                </tbody>
                                {!logisticData.loading && logisticData.data && logisticData.data.length > 0 && (
                                    <tfoot>
                                        <tr className="fw-bold text-center">
                                            <td>Total</td>
                                            <td>{totalValue}</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                    <div className="col-6 p0 mt-3 mb-1">
                        {logisticData.loading
                            ? <Loader />
                            : (logisticData.data && totalValue > 0 ? (
                                <Chart
                                    chartType="PieChart"
                                    data={[['Status Name', 'Logistic Count']].concat(logisticData.data.map((d) => { return [d.name, d.count]; }))}
                                    options={{
                                        legend: { position: 'labeled', labeledValueText: 'both' },
                                        pieSliceText: 'none',
                                        chartArea: { width: '90%', height: '90%' },
                                    }}
                                    rootProps={{ 'data-testid': '1' }} />
                            ) : null)
                        }
                    </div>
                </div>
                <div className="col-sm-12 text-muted fs11">
                    Report as of {logisticData.remark_time}
                </div>
            </div>
            <UpdateMyDashboardForm ref={updateMyDashboardRef} />
        </div>
    );
};

export default LogisticWidget;

